import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './ComponentInventory.css';

const ComponentInventory = () => {
  const [lastUpdated, setLastUpdated] = useState('N/A');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleFiles, setGoogleFiles] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [importing, setImporting] = useState({});

  useEffect(() => {
    fetchLastUpdatedDate();
  }, []);

  const fetchLastUpdatedDate = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/component-inventory/last-updated`);
      setLastUpdated(response.data.lastUpdated || 'N/A');
    } catch (error) {
      console.error('Error fetching last updated date:', error);
      setLastUpdated('N/A');
    }
  };

  const handleRefreshGoogleFiles = async () => {
    setRefreshing(true);
    setError('');
    setGoogleFiles([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/component-inventory/refresh-google-files`);
      
      if (response.data.success) {
        setGoogleFiles(response.data.files);
      } else {
        setError(response.data.error || 'Failed to refresh Google files');
      }
    } catch (error) {
      console.error('Error refreshing Google files:', error);
      setError(error.response?.data?.error || 'Failed to refresh Google files');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    console.log('Download button clicked:', { fileId, fileName });
    setDownloading(prev => ({ ...prev, [fileId]: true }));
    
    try {
      const url = `${API_BASE_URL}/api/component-inventory/download-file/${fileId}`;
      console.log('Making download request to:', url);
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      console.log('Download response received:', response);
      
      // Create a download link
      const url2 = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url2;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url2);
      
      console.log('Download completed successfully');
      
    } catch (error) {
      console.error('Error downloading file:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      setError('Failed to download file: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setDownloading(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const handleImportFile = async (fileId, fileName) => {
    setImporting(prev => ({ ...prev, [fileId]: true }));
    
    try {
      const url = `${API_BASE_URL}/api/component-inventory/import-file/${fileId}`;
      console.log('Making import request to:', url);
      
      const response = await axios.post(url);
      
      console.log('Import response received:', response);
      
      if (response.data.success) {
        const { total_imported, duplicates_updated, filename, inventory_read_date } = response.data;
        alert(`✅ Import completed successfully!\n\n` +
              `File: ${filename}\n` +
              `Date: ${inventory_read_date}\n` +
              `New records imported: ${total_imported}\n` +
              `Existing records updated: ${duplicates_updated}\n\n` +
              `Total records processed: ${total_imported + duplicates_updated}`);
      } else {
        alert(`❌ Import failed: ${response.data.error}`);
      }
      
    } catch (error) {
      console.error('Error importing file:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert(`❌ Import failed: ${errorMessage}`);
    } finally {
      setImporting(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatFileSize = (size) => {
    if (!size || size === 'Unknown') return 'Unknown';
    const bytes = parseInt(size);
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="component-inventory-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Sundeep Component Inventory</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>View and manage component inventory at Sundeep</li>
              <li>Import latest inventory files from Google Drive</li>
              <li>Track component levels and status</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        <div className="inventory-info">
          <div className="info-card">
            <h2>Component Inventory Management</h2>
            <p>Component inventory data is managed through automated imports from Google Drive.</p>
            
            <div className="last-updated-info">
              <strong>Last Updated:</strong> {lastUpdated}
            </div>
            
            <div className="refresh-section">
              <button 
                className="refresh-button"
                onClick={handleRefreshGoogleFiles}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing...' : 'Refresh Google Files'}
              </button>
              
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              
              {googleFiles.length > 0 && (
                <div className="google-files-section">
                  <h3>Available Files in Sundeep Reports Folder ({googleFiles.length} files)</h3>
                  <div className="files-table">
                    <table>
                      <thead>
                        <tr>
                          <th>File Name</th>
                          <th>Modified Date</th>
                          <th>Created Date</th>
                          <th>Size</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {googleFiles.map((file, index) => (
                          <tr key={file.id || index}>
                            <td>{file.name}</td>
                            <td>{formatDate(file.modifiedTime)}</td>
                            <td>{formatDate(file.createdTime)}</td>
                            <td>{formatFileSize(file.size)}</td>
                            <td className="action-buttons">
                              <button
                                className="download-button"
                                onClick={() => handleDownloadFile(file.id, file.name)}
                                disabled={downloading[file.id]}
                              >
                                {downloading[file.id] ? 'Downloading...' : 'Download'}
                              </button>
                              <button
                                className="import-button"
                                onClick={() => handleImportFile(file.id, file.name)}
                                disabled={importing[file.id]}
                              >
                                {importing[file.id] ? 'Importing...' : 'Import'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentInventory; 